import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Bot, Users, Shield, CheckCircle2, XCircle, Loader2, 
  Settings, ExternalLink, RefreshCw, Mic, MicOff, Sparkles, MessageSquare,
  Briefcase, FileText, Scale, Phone, BarChart3, Globe, ArrowUpRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

// Agent icons/colors by category
const agentStyles = {
  sony: { color: 'bg-blue-500', icon: '📱', gradient: 'from-blue-500 to-cyan-500' },
  sales_associate: { color: 'bg-green-500', icon: '💼', gradient: 'from-green-500 to-emerald-500' },
  eva: { color: 'bg-purple-500', icon: '👩‍💼', gradient: 'from-purple-500 to-violet-500' },
  penny: { color: 'bg-pink-500', icon: '✍️', gradient: 'from-pink-500 to-rose-500' },
  linda: { color: 'bg-amber-500', icon: '⚖️', gradient: 'from-amber-500 to-orange-500' },
  rachel: { color: 'bg-cyan-500', icon: '📞', gradient: 'from-cyan-500 to-teal-500' },
  stan: { color: 'bg-indigo-500', icon: '📊', gradient: 'from-indigo-500 to-blue-500' },
  full_platform: { color: 'bg-gradient-to-r from-purple-600 to-blue-600', icon: '🌐', gradient: 'from-purple-600 to-blue-600' },
};

const categoryIcons = {
  marketing: <MessageSquare className="w-4 h-4" />,
  sales: <Briefcase className="w-4 h-4" />,
  administration: <Phone className="w-4 h-4" />,
  content: <FileText className="w-4 h-4" />,
  legal: <Scale className="w-4 h-4" />,
  analytics: <BarChart3 className="w-4 h-4" />,
  full_access: <Globe className="w-4 h-4" />,
};

const AIWorkforcePage = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [allAgents, setAllAgents] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const [merlinUrl, setMerlinUrl] = useState('');
  const [marblismLoginUrl, setMarblismLoginUrl] = useState('');
  const [marblismOrgUrl, setMarblismOrgUrl] = useState('');
  const [hasFullPlatformAccess, setHasFullPlatformAccess] = useState(false);
  
  const [showManageDialog, setShowManageDialog] = useState(false);
  const [staffAccess, setStaffAccess] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [updating, setUpdating] = useState(false);
  
  // Voice command state (Super Admin only)
  const [voiceEnabled, setVoiceEnabled] = useState(false);

  const fetchAgents = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/ai-workforce/agents');
      setAgents(res.data.agents || []);
      setIsSuperAdmin(res.data.is_super_admin || false);
      setCanManage(res.data.can_manage || false);
      setMerlinUrl(res.data.merlin_url || '');
      setMarblismLoginUrl(res.data.marblism_login_url || '');
      setMarblismOrgUrl(res.data.marblism_org_url || '');
      setHasFullPlatformAccess(res.data.has_full_platform_access || false);
      
      if (res.data.is_super_admin) {
        const allRes = await api.get('/ai-workforce/all-agents');
        setAllAgents(allRes.data.agents || []);
      }
    } catch (err) {
      toast.error('Failed to load AI agents');
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchStaffAccess = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await api.get('/ai-workforce/staff-access');
      setStaffAccess(res.data.staff_access || []);
      if (res.data.all_agents) {
        setAllAgents(res.data.all_agents);
      }
    } catch (err) {
      console.error('Error fetching staff access:', err);
    }
  }, [api, isSuperAdmin]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchStaffAccess();
    }
  }, [isSuperAdmin, fetchStaffAccess]);

  const openAgentAccess = (staff) => {
    setSelectedStaff(staff);
    setSelectedAgents(staff.granted_agents || []);
    setShowManageDialog(true);
  };

  const toggleAgentSelection = (agentId) => {
    setSelectedAgents(prev => 
      prev.includes(agentId) 
        ? prev.filter(id => id !== agentId)
        : [...prev, agentId]
    );
  };

  const saveAgentAccess = async () => {
    if (!selectedStaff) return;
    try {
      setUpdating(true);
      await api.post('/ai-workforce/grant-access', {
        user_id: selectedStaff.user_id,
        agent_ids: selectedAgents
      });
      toast.success('Agent access updated');
      setShowManageDialog(false);
      fetchStaffAccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update access');
    } finally {
      setUpdating(false);
    }
  };

  const logAgentUsage = async (agent) => {
    try {
      await api.post('/ai-workforce/log-usage', {
        agent_id: agent.id,
        agent_name: agent.name,
        action: 'accessed'
      });
    } catch (err) {
      console.error('Error logging usage:', err);
    }
  };

  // Open agent in new tab (iframes blocked by these sites)
  const openAgent = (agent) => {
    logAgentUsage(agent);
    const url = agent.url || marblismOrgUrl;
    window.open(url, '_blank', 'noopener,noreferrer');
    toast.success(`Opening ${agent.name} in new tab`);
  };

  const openMerlin = () => {
    window.open(merlinUrl, '_blank', 'noopener,noreferrer');
    toast.success('Opening Merlin AI in new tab');
  };

  const openFullPlatform = () => {
    window.open(marblismOrgUrl, '_blank', 'noopener,noreferrer');
    toast.success('Opening Marblism Platform in new tab');
  };

  // Voice Command Setup (Super Admin only)
  const toggleVoiceCommand = () => {
    if (!isSuperAdmin) {
      toast.error('Voice commands are only available for Super Admin');
      return;
    }
    
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      toast.error('Voice recognition is not supported in this browser');
      return;
    }
    
    setVoiceEnabled(!voiceEnabled);
    if (!voiceEnabled) {
      toast.success('Voice commands enabled. Say "Hey Merlin" to activate.');
    }
  };

  // Group agents by category
  const groupedAgents = agents.reduce((acc, agent) => {
    const category = agent.category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(agent);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">AI Workforce</h1>
          <p className="text-slate-600">Access your AI assistants powered by Marblism AI</p>
        </div>
        <div className="flex gap-2">
          {isSuperAdmin && (
            <>
              <Button 
                variant={voiceEnabled ? "default" : "outline"}
                onClick={toggleVoiceCommand}
                className={voiceEnabled ? "bg-green-500 hover:bg-green-600" : ""}
              >
                {voiceEnabled ? <Mic className="w-4 h-4 mr-2" /> : <MicOff className="w-4 h-4 mr-2" />}
                Voice Commands
              </Button>
              <Button onClick={() => { fetchStaffAccess(); setShowManageDialog(true); setSelectedStaff(null); }}>
                <Users className="w-4 h-4 mr-2" />
                Manage Staff Access
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Info Notice */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <ExternalLink className="w-5 h-5 text-blue-600" />
            <div>
              <p className="font-medium text-blue-800">AI Tools Open in New Tabs</p>
              <p className="text-sm text-blue-700">Marblism and Merlin AI will open in new browser tabs for the best experience. Make sure pop-ups are allowed.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Super Admin Notice */}
      {isSuperAdmin && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">Super Admin - Full Platform Access</p>
                <p className="text-sm text-amber-700">You have access to all AI agents, Merlin AI, and the full Marblism platform. Use "Manage Staff Access" to grant specific agent access to staff members.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="agents" className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-lg">
          <TabsTrigger value="agents" className="flex items-center gap-2">
            <Bot className="w-4 h-4" />
            AI Agents
          </TabsTrigger>
          {isSuperAdmin && (
            <TabsTrigger value="platform" className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Full Platform
            </TabsTrigger>
          )}
          {isSuperAdmin && (
            <TabsTrigger value="merlin" className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Merlin AI
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="agents" className="mt-6">
          {/* No Access Message */}
          {!isSuperAdmin && agents.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <Bot className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-700">No AI Agents Assigned</h3>
                <p className="text-slate-500 mt-2">Contact Super Admin to request access to AI workforce agents.</p>
              </CardContent>
            </Card>
          )}

          {/* Agent Cards by Category */}
          {Object.keys(groupedAgents).length > 0 && (
            <div className="space-y-6">
              {Object.entries(groupedAgents).map(([category, categoryAgents]) => (
                <div key={category}>
                  <div className="flex items-center gap-2 mb-3">
                    {categoryIcons[category]}
                    <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">
                      {category.replace(/_/g, ' ')}
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {categoryAgents.filter(a => a.id !== 'full_platform').map((agent) => {
                      const style = agentStyles[agent.id] || { color: 'bg-slate-500', icon: '🤖', gradient: 'from-slate-500 to-gray-500' };
                      return (
                        <Card 
                          key={agent.id} 
                          className="hover:shadow-xl transition-all duration-300 cursor-pointer group overflow-hidden"
                          onClick={() => openAgent(agent)}
                        >
                          <div className={`h-2 bg-gradient-to-r ${style.gradient}`} />
                          <CardHeader className="pb-2">
                            <div className="flex items-center gap-3">
                              <div className={`w-12 h-12 rounded-xl ${style.color} flex items-center justify-center text-2xl shadow-lg group-hover:scale-110 transition-transform`}>
                                {style.icon}
                              </div>
                              <div className="flex-1">
                                <CardTitle className="text-lg flex items-center gap-2">
                                  {agent.name}
                                  <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-[#1e3a5f] transition-colors" />
                                </CardTitle>
                                <CardDescription className="text-sm">{agent.role}</CardDescription>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <p className="text-sm text-slate-600 mb-4">{agent.description}</p>
                            <Button className="w-full bg-[#1e3a5f] hover:bg-[#1e3a5f]/90" variant="default">
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Open in New Tab
                            </Button>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {isSuperAdmin && (
          <TabsContent value="platform" className="mt-6">
            <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50">
              <CardHeader>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
                    <Globe className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-xl text-indigo-900">Full Marblism AI Platform</CardTitle>
                    <CardDescription className="text-indigo-700">Access all AI agents and features in one place</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-slate-600">
                  The full Marblism AI platform gives you access to all available AI agents including Eva (Executive Assistant), 
                  Penny (Blog Writer), Linda (Legal Associate), Rachel (Receptionist), Stan (Data Analyst), and more.
                </p>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {allAgents.filter(a => a.id !== 'full_platform').map((agent) => {
                    const style = agentStyles[agent.id] || { color: 'bg-slate-500', icon: '🤖' };
                    return (
                      <div key={agent.id} className="flex items-center gap-2 p-2 bg-white rounded-lg border">
                        <span className="text-xl">{style.icon}</span>
                        <div>
                          <p className="text-sm font-medium">{agent.name}</p>
                          <p className="text-xs text-slate-500">{agent.role}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button 
                    className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                    onClick={openFullPlatform}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open Marblism Platform
                  </Button>
                </div>
                
                <p className="text-xs text-slate-500 text-center">
                  Opens in a new browser tab • Login with your Marblism account
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {isSuperAdmin && (
          <TabsContent value="merlin" className="mt-6">
            <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50">
              <CardHeader>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-xl text-purple-900">Merlin AI</CardTitle>
                    <CardDescription className="text-purple-700">Your personal AI assistant - Super Admin exclusive</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-slate-600">
                  Merlin AI is your intelligent assistant for complex queries, research, and creative tasks. 
                  This tool is exclusively available to the Super Admin and provides powerful AI capabilities.
                </p>
                <div className="flex gap-3">
                  <Button 
                    className="flex-1 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
                    onClick={openMerlin}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open Merlin AI
                  </Button>
                </div>
                <p className="text-xs text-slate-500 text-center">
                  Opens in a new browser tab • Login with your registered Gmail account
                </p>
              </CardContent>
            </Card>

            <div className="mt-6 p-4 bg-slate-100 rounded-lg">
              <h3 className="font-medium text-slate-700 mb-2">Voice Command Feature</h3>
              <p className="text-sm text-slate-600">
                Enable voice commands to interact with Merlin AI hands-free. 
                Say "Hey Merlin" followed by your command.
              </p>
              <Button 
                variant={voiceEnabled ? "default" : "outline"}
                onClick={toggleVoiceCommand}
                className={`mt-3 ${voiceEnabled ? "bg-green-500 hover:bg-green-600" : ""}`}
              >
                {voiceEnabled ? <Mic className="w-4 h-4 mr-2" /> : <MicOff className="w-4 h-4 mr-2" />}
                {voiceEnabled ? 'Voice Commands Active' : 'Enable Voice Commands'}
              </Button>
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Manage Staff Access Dialog - Super Admin Only */}
      <Dialog open={showManageDialog && isSuperAdmin && !selectedStaff} onOpenChange={setShowManageDialog}>
        <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage AI Workforce Access</DialogTitle>
            <DialogDescription>
              Grant or revoke AI agent access for staff members. You can give access to individual agents or the full Marblism platform.
            </DialogDescription>
          </DialogHeader>
          
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Staff Member</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Granted Agents</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {staffAccess.map((staff) => (
                <TableRow key={staff.user_id} className={staff.is_super_admin ? 'bg-amber-50' : ''}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{staff.name}</p>
                      <p className="text-xs text-slate-500">{staff.email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {staff.role?.replace(/_/g, ' ') || 'N/A'}
                    </Badge>
                    {staff.is_super_admin && (
                      <Badge className="ml-1 bg-amber-500">Super Admin</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {staff.has_full_access ? (
                        <Badge className="bg-gradient-to-r from-indigo-500 to-purple-500">Full Platform + Merlin</Badge>
                      ) : staff.granted_agents?.length > 0 ? (
                        staff.granted_agents.map(aid => {
                          const agent = allAgents.find(a => a.id === aid);
                          const style = agentStyles[aid] || { icon: '🤖' };
                          return agent ? (
                            <Badge key={aid} variant="secondary" className="text-xs">
                              {style.icon} {agent.name}
                            </Badge>
                          ) : null;
                        })
                      ) : (
                        <span className="text-slate-400 text-sm">No access</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    {!staff.is_super_admin && (
                      <Button size="sm" onClick={() => openAgentAccess(staff)}>
                        <Settings className="w-3 h-3 mr-1" />
                        Edit Access
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManageDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Staff Agent Access Dialog */}
      <Dialog open={showManageDialog && selectedStaff !== null} onOpenChange={(open) => { if (!open) setSelectedStaff(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Agent Access for {selectedStaff?.name}</DialogTitle>
            <DialogDescription>
              Select which AI agents this staff member can access. You can grant individual agents or full platform access.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-3 max-h-[50vh] overflow-y-auto">
            {allAgents.map((agent) => {
              const style = agentStyles[agent.id] || { color: 'bg-slate-500', icon: '🤖' };
              const isSelected = selectedAgents.includes(agent.id);
              
              return (
                <div
                  key={agent.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    isSelected ? 'border-[#1e3a5f] bg-blue-50' : 'border-slate-200 hover:bg-slate-50'
                  } ${agent.id === 'full_platform' ? 'border-2 border-dashed' : ''}`}
                  onClick={() => toggleAgentSelection(agent.id)}
                >
                  <Checkbox checked={isSelected} />
                  <div className={`w-10 h-10 rounded-full ${agent.id === 'full_platform' ? 'bg-gradient-to-r from-indigo-500 to-purple-500' : style.color} flex items-center justify-center text-xl`}>
                    {style.icon}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{agent.name}</p>
                    <p className="text-sm text-slate-500">{agent.role}</p>
                    {agent.id === 'full_platform' && (
                      <p className="text-xs text-indigo-600 mt-1">Grants access to ALL Marblism agents</p>
                    )}
                  </div>
                  {isSelected && <CheckCircle2 className="w-5 h-5 text-green-500" />}
                </div>
              );
            })}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedStaff(null)}>Cancel</Button>
            <Button onClick={saveAgentAccess} disabled={updating}>
              {updating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIWorkforcePage;
